import {Box, Typography} from '@mui/material';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {ReportData} from './model';
import {getColumnByType, getReportDataByType} from './utils';
import {t} from 'i18next';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {getProcessingExceptionReportDetails} from '../../../../api/ReportingManagement';
import {
  failedToLoadStatusCodes,
  TABLE_PER_PAGE_5000_ROW,
} from '../../../../constants/constants';
import AurDataGridLoadingOverlay from '../../../common/aur-data-grid-loading-overlay/AurDataGridLoadingOverlay';
import AurDataGridErrorOverlay from '../../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import AurDataGridPro from '../../../common/aur-data-grid-pro/AurDataGridPro';
import {isEmptyInstance} from '../../../../utils/utils';
import AurDataGridNoRowsOverlay from '../../../common/aur-data-grid-no-rows-overlay/AurDataGridNoRowsOverlay';

interface ReportSideBarProps {
  activeReport: string;
}

const abortControllers: Map<string, AbortController> = new Map();

const ReportTableContent = (props: ReportSideBarProps) => {
  const {activeReport} = props;

  const [isLoading, setIsLoading] = useState(false);
  const [custodianData, setCustodianData] = useState<any>({});

  const {clientId, projectId, workspaceId} = useSelector(
    (state: RootState) => state.reports
  );

  const dataSetName = useSelector(
    (state: RootState) => state.reports.workspaceDataSetName
  );

  const [
    isFailedToLoadExceptionReportDetails,
    setIsFailedToLoadExceptionReportDetails,
  ] = useState<boolean>(false);

  useEffect(() => {
    // Clean up
    return () => {
      abortControllers.forEach((controller) => {
        controller.abort();
      });
      abortControllers.clear();
    };
  }, []);

  const hasData = useMemo(
    () => Object.keys(custodianData).length > 0,
    [custodianData]
  );

  const formatRowData = useCallback((data: any) => {
    const newData = data.map((row: any, index: number) => ({
      ...row,
      id: row.id ? row.id : index,
    }));
    const groupedData = newData.reduce((acc: any, item: any) => {
      const custodian = item.custodianDisplayName;
      if (!acc[custodian]) {
        acc[custodian] = [];
      }
      acc[custodian].push(item);
      return acc;
    }, {});

    const sortedGrouped = Object.keys(groupedData)
      .sort()
      .reduce((acc: any, key: any) => {
        acc[key] = groupedData[key];
        return acc;
      }, {});
    setCustodianData(sortedGrouped);
  }, []);

  const fetchExceptionReportDetails = useCallback(async () => {
    const url = '/processing-exceptions';
    if (abortControllers.has(url)) {
      abortControllers.get(url)?.abort();
    }

    const controller = new AbortController();
    abortControllers.set(url, controller);

    setIsLoading(true);
    try {
      setCustodianData({});
      setIsFailedToLoadExceptionReportDetails(false);
      const response = await getProcessingExceptionReportDetails(
        TABLE_PER_PAGE_5000_ROW,
        1,
        clientId,
        projectId,
        workspaceId,
        dataSetName,
        getReportDataByType(activeReport).type,
        controller.signal
      );

      abortControllers.delete(url);
      const rowData = response?.data?.data?.processingExceptions || [];
      !isEmptyInstance(rowData) && formatRowData(rowData);
    } catch (error: unknown) {
      const e = error as {name: string; status: number};
      // Handled cancel case as we are cancelling request once existing is running and user try to load other data
      if (e.name === 'CanceledError') {
        return;
      }
      abortControllers.delete(url);
      setCustodianData({});
      if (failedToLoadStatusCodes.includes(e.status))
        setIsFailedToLoadExceptionReportDetails(true);
    } finally {
      setIsLoading(false);
    }
  }, [
    activeReport,
    clientId,
    dataSetName,
    formatRowData,
    projectId,
    workspaceId,
  ]);

  useEffect(() => {
    if (projectId && clientId && workspaceId && dataSetName && activeReport) {
      fetchExceptionReportDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, projectId, workspaceId, dataSetName, activeReport]);

  const totalExceptions = useCallback(
    (data: any[]) =>
      data.reduce((sum, row: any) => sum + (parseInt(row?.total ?? 0) || 0), 0),
    []
  );

  //for adding dynamic height for multiple grids
  const calculateGridHeight = useCallback((length: number) => {
    const rowHeight = 28; //height of each row
    return (length + 1) * rowHeight + 44; //adding height of headers
  }, []);

  const columns = useMemo(() => getColumnByType(activeReport), [activeReport]);

  const reportData: ReportData = useMemo(
    () => getReportDataByType(activeReport),
    [activeReport]
  );

  const noRowsOverlay = useMemo(
    () =>
      isLoading
        ? AurDataGridLoadingOverlay
        : isFailedToLoadExceptionReportDetails
          ? AurDataGridErrorOverlay
          : AurDataGridNoRowsOverlay,
    [isFailedToLoadExceptionReportDetails, isLoading]
  );

  return (
    <>
      <Box className="report-table-content">
        <div className="report-top">
          <Typography className="typo-text subhead1_size">
            {reportData?.title}
          </Typography>
          <Typography className="typo-text body2_size ">
            {reportData?.description}
          </Typography>
        </div>
        <div className="grid">
          {hasData ? (
            Object.keys(custodianData).map((custodianName) => (
              <Box key={custodianName}>
                <div className="user-total">
                  <Typography className="typo-text subhead3_size">
                    {custodianName || ''}
                  </Typography>
                  {reportData?.total && (
                    <Typography className="typo-text subhead3_size">
                      {t('reports.processingExcception.total')}&nbsp;
                      {totalExceptions(custodianData[custodianName])}
                    </Typography>
                  )}
                </div>
                {/**Adding dynamic height to the container based on the number of rows*/}
                <div
                  style={{
                    overflow: 'hidden',
                    height: `${calculateGridHeight(custodianData[custodianName].length)}px`,
                  }}
                >
                  <AurDataGridPro
                    className="pe-reports-grid"
                    rows={custodianData[custodianName]}
                    getRowId={(row) => row.id}
                    columns={columns}
                    showPagination={false}
                    visibleToolTip={false}
                    checkBoxVisible={false}
                    entityType={''}
                    pointer={true}
                    rowHeight={20}
                    currentPage={1}
                    itemsPerPage={custodianData[custodianName].length}
                    onErrorRefresh={fetchExceptionReportDetails}
                    autoPageSize
                    noRowsOverlay={noRowsOverlay}
                    data-testid="report-table-data-grid"
                  />
                </div>
              </Box>
            ))
          ) : (
            <AurDataGridPro
              key={`${isLoading}`}
              className="pe-reports-grid"
              rows={[]}
              columns={columns}
              showPagination={false}
              checkBoxVisible={false}
              entityType={''}
              pointer={true}
              visibleToolTip={false}
              onErrorRefresh={fetchExceptionReportDetails}
              noRowsOverlay={noRowsOverlay}
              data-testid="report-table-data-grid"
            />
          )}
        </div>
      </Box>
    </>
  );
};

export default ReportTableContent;
